import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import Reveal from "../Reveal";
import { BrowserFrame, PhoneFrame } from "../Frames";

const SHOWCASES = [
  {
    id: "scheduling",
    eyebrow: "Jobs & Dispatch",
    title: "Schedule and dispatch jobs in seconds",
    body: "A drag-and-drop calendar built for dispatchers. See every technician's day, assign the right person, and keep customers informed automatically.",
    bullets: [
      "Calendar, list, and timeline views for day and week planning",
      "Assign technicians with live availability at a glance",
      "Color-coded job statuses from estimate to paid",
      "Automatic SMS confirmations and reminders for every booking",
    ],
    img: "/marketing/fieldz-ai-job-scheduling-calendar.webp",
    alt: "Fieldz AI job scheduling calendar with drag-and-drop dispatch",
    overlay: "/marketing/fieldz-ai-dispatch-timeline.webp",
    overlayAlt: "Fieldz AI dispatch timeline view by technician",
  },
  {
    id: "crm",
    eyebrow: "Customer CRM",
    title: "Every customer, with their full story",
    body: "Complete service history, notes, addresses, and communications on one profile — so anyone who answers the phone knows exactly who's calling.",
    bullets: [
      "Full job, invoice, call, and message history per customer",
      "Multiple service addresses under one customer record",
      "Notes and attachments your whole team can see",
      "Global search across customers, jobs, and invoices",
    ],
    img: "/marketing/fieldz-ai-customer-crm.webp",
    alt: "Fieldz AI customer CRM with searchable customer database",
    overlay: "/marketing/fieldz-ai-customer-profile.webp",
    overlayAlt: "Fieldz AI customer profile with service history",
  },
  {
    id: "phone-sms",
    eyebrow: "Business Phone + SMS",
    title: "A built-in phone system your customers recognize",
    body: "Get dedicated business numbers, take calls in the browser or mobile app, and text customers from the same number they already know. No extra hardware.",
    bullets: [
      "In-app dialer with call history, recordings, and voicemail",
      "Two-way SMS conversations tied to each customer",
      "Call masking keeps technicians' personal numbers private",
      "Smart routing: ring everyone, sequential, or round-robin with business hours",
    ],
    img: "/marketing/fieldz-ai-call-history.webp",
    alt: "Fieldz AI business phone call history and in-app dialer",
    overlay: "/marketing/fieldz-ai-sms-messaging.webp",
    overlayAlt: "Fieldz AI two-way SMS messaging inbox",
  },
  {
    id: "invoicing",
    eyebrow: "Estimates, Invoices & Payments",
    title: "From estimate to paid — without the paperwork",
    body: "Send professional estimates, collect e-signatures, convert to invoices in one click, and take card payments online or in the field.",
    bullets: [
      "Branded estimates and invoices with line items and discounts",
      "E-signature approval right from the customer's phone",
      "Card payments powered by Stripe — online or on-site",
      "Deposits, partial payments, and automatic receipts built in",
    ],
    img: "/marketing/fieldz-ai-invoice-software.webp",
    alt: "Fieldz AI invoicing software with online payments",
  },
  {
    id: "reports",
    eyebrow: "Financial Reporting",
    title: "Know exactly what every job earns you",
    body: "Real profit visibility: revenue, parts, technician commissions, processing fees, and tax — broken down per job, per tech, and per location.",
    bullets: [
      "Job-level profitability with parts and commission costs",
      "Technician commission tracking with one-click settlements",
      "Tax collected tracked separately — never inflates revenue",
      "Export any report to CSV or PDF",
    ],
    img: "/marketing/fieldz-ai-financial-reports.webp",
    alt: "Fieldz AI financial reports with job profitability and commissions",
  },
  {
    id: "mobile-app",
    eyebrow: "Technician Mobile Portal",
    title: "Your technicians' entire day, in their pocket",
    body: "Techs see assigned jobs, navigate to the address, update statuses, attach photos, create estimates, and collect payments — all from their phone.",
    bullets: [
      "Job list with statuses, addresses, and one-tap navigation",
      "Create estimates and collect card payments in the field",
      "Photos and notes sync to the office instantly",
      "Techs see their own earnings and commissions",
    ],
    img: "/marketing/fieldz-ai-technician-app-dashboard.webp",
    alt: "Fieldz AI technician mobile app dashboard",
    phone: true,
    phoneSecondary: "/marketing/fieldz-ai-technician-portal.webp",
    phoneSecondaryAlt: "Fieldz AI technician portal job view",
  },
  {
    id: "automations",
    eyebrow: "Automations & Lead Capture",
    title: "Follow-ups that happen without you",
    body: "Put reminders, review requests, and lead intake on autopilot so no customer slips through the cracks — even on your busiest days.",
    bullets: [
      "Appointment reminders and follow-ups sent automatically",
      "Review requests after completed jobs",
      "Embeddable lead forms that create jobs for you",
      "Trigger SMS or email on any job status change",
    ],
    img: "/marketing/fieldz-ai-automations.webp",
    alt: "Fieldz AI automations for reminders, reviews, and lead capture",
  },
  {
    id: "multi-location",
    eyebrow: "Multi-Location & Enterprise",
    title: "Every branch on one dashboard",
    body: "Built for businesses running 2+ locations: consolidated reporting, location-level permissions, and dispatch that routes work to the right branch.",
    bullets: [
      "Cross-location dashboard with per-branch filtering",
      "Route jobs and phone lines to the right location",
      "Branch managers only see their location's data",
      "One subscription and consolidated reporting for all branches",
    ],
    img: "/marketing/fieldz-ai-multi-location-dashboard.webp",
    alt: "Fieldz AI multi-location dashboard with consolidated reporting",
  },
];

const Media = ({ item }) => {
  if (item.phone) {
    return (
      <div className="relative flex justify-center items-start gap-0 py-4">
        <PhoneFrame src={item.img} alt={item.alt} className="w-[220px] sm:w-[260px] z-10" />
        {item.phoneSecondary && (
          <PhoneFrame
            src={item.phoneSecondary}
            alt={item.phoneSecondaryAlt}
            className="w-[190px] sm:w-[230px] -ml-14 mt-14 hidden sm:block"
          />
        )}
      </div>
    );
  }
  return (
    <div className={`relative ${item.overlay ? "pb-10 md:pb-14" : ""}`}>
      <div className="rounded-3xl bg-gradient-to-tr from-blue-50/60 via-white to-indigo-50/40 p-3 sm:p-5 border border-slate-200/60">
        <BrowserFrame src={item.img} alt={item.alt} />
      </div>
      {item.overlay && (
        <div className="hidden md:block absolute bottom-0 -right-4 lg:-right-8 w-[46%] rounded-xl overflow-hidden border border-slate-200 shadow-[0_20px_40px_-10px_rgba(15,23,42,0.28)] bg-white">
          <img src={item.overlay} alt={item.overlayAlt} loading="lazy" width={1440} height={900} className="w-full block" />
        </div>
      )}
    </div>
  );
};

const ShowcaseSections = () => (
  <div data-testid="product-showcases">
    {SHOWCASES.map((item, i) => {
      const flip = i % 2 === 1;
      return (
        <section
          key={item.id}
          id={item.id}
          className={`py-16 md:py-24 ${i % 2 === 0 ? "bg-white" : "bg-[var(--mk-bg-2)]"}`}
          data-testid={`showcase-${item.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <Reveal className={flip ? "lg:order-2" : ""}>
                <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--mk-blue)] mb-4">{item.eyebrow}</p>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.15] mb-4">{item.title}</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-7">{item.body}</p>
                <ul className="space-y-3 mb-8">
                  {item.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-[var(--mk-blue)] shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-1.5 text-[15px] font-semibold text-[var(--mk-blue)] hover:text-[var(--mk-blue-hover)] group"
                  data-testid={`showcase-cta-${item.id}`}
                >
                  Try it free for 7 days
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Reveal>
              <Reveal delay={1} className={flip ? "lg:order-1" : ""}>
                <Media item={item} />
              </Reveal>
            </div>
          </div>
        </section>
      );
    })}
  </div>
);

export default ShowcaseSections;
